import {
  Button,
  Card,
  DateRadioChipGroup,
  FormField,
  Heading,
  Paragraph,
  RadioChipGroup,
  RadioChipOptions,
  Select,
  Text,
} from '@expressable/syntax-ds';
import { format } from 'date-fns';
import { EvaluationTimeSlot } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isEmpty } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useController, UseControllerReturn, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PageSizeConfig } from './CalendarPageConfig';
import { DateChipAdornment } from './DateChipAdornment';
import {
  ContentfulTherapistSummary,
  TherapistSummaryProps,
} from 'components/ContentfulTherapistCard/ContentfulTherapistSummary';
import { captureException } from '@sentry/react';
import { EvaluationScheduleFormType } from 'domains/signupDirect/schemas/evaluationScheduleSchema';

type Props = {
  onChange?: (timeSlot?: EvaluationTimeSlot) => void;
  name: string;
  calendarData: Extract<UseEvaluationCalendarResult, { isLoading: false }>;
  therapistBadgesData: Omit<TherapistSummaryProps, 'therapistName'>;
};

// Experimental component, currently only meant to be used in single page signup. Will be rewritten and ported to syntax

const DAY_PICKER_ID = 'dayPickerInput';

export const EvaluationCalendarPicker: FC<Props> = ({ onChange, name, calendarData, therapistBadgesData }) => {
  // @TODO(juan): types need to be cleaned up here, and properly separate the component from RHF schema
  const {
    field,
    fieldState: { error },
  } = useController({ name: name as 'timeSlot' }) as UseControllerReturn<
    Partial<EvaluationScheduleFormType>,
    'timeSlot'
  >;
  const { isSubmitting } = useFormState();
  const { currentAvailability: currentTimelineSlots, dayIndex, onChangeDay } = calendarData;

  const { availabilityByTherapist, day: currentDay } = currentTimelineSlots;

  // Define if there are no available slots (depends on the timelineSlots and if all requests are resolved)
  const noAvailableSlots = availabilityByTherapist.length === 0;

  const { displayTimezone, setDisplayTimezone, format: formatInTimezone } = useDisplayTimezone();

  const dayOptions: RadioChipOptions = useMemo(() => {
    return calendarData.allTimeSlots
      .map(({ day, therapistCount, timeSlotCount }, index) => {
        return {
          value: `${index}`,
          date: day,
          adornment: <DateChipAdornment count={therapistCount} disabled={timeSlotCount === 0} />,
          disabled: timeSlotCount === 0,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [calendarData]);

  const onChangeTimeSlot = (slot?: EvaluationTimeSlot) => {
    onChange?.(slot);
    field.onChange(slot);
  };

  const { t } = useTranslation();

  const GhostAnchor = () => <div ref={field.ref} className="hidden" />;

  // Start day carousel on first day with available slots
  const [startIndex, setStartIndex] = useState<number | undefined>();

  useEffect(() => {
    const dayOptionIndex = dayOptions.findIndex(option => !option.disabled);

    if (dayOptionIndex !== -1 && dayIndex < dayOptionIndex) {
      onChangeDay(dayOptionIndex);
      setStartIndex(dayOptionIndex);
    }
  }, [dayOptions, dayIndex, onChangeDay]);

  return (
    <>
      <FormField label={t('directSignup.schedule.timezone.label')} name={!displayTimezone ? name : undefined}>
        <Select
          options={timeZoneOptions}
          onChange={selectedValue => {
            if (isEmpty(selectedValue)) {
              onChangeTimeSlot(undefined);
              setDisplayTimezone(undefined);
            } else {
              setDisplayTimezone(timeZoneOptions.find(({ value }) => value === selectedValue) as TimezoneOption);
            }
          }}
          value={displayTimezone?.value}
          disabled={isSubmitting}
          standalone
        />
      </FormField>

      {!displayTimezone && (
        <Paragraph>
          {t('directSignup.schedule.noTimezone')} <GhostAnchor />
        </Paragraph>
      )}

      {displayTimezone && (
        <>
          {/* @TODO(juan): this formField is a workaround until syntax supports hiding/pointing errors to specific inputs dynamically */}

          <FormField
            name={noAvailableSlots ? name : undefined}
            label={t('directSignup.schedule.dayPicker.label')}
            config={{ order: { error: 'top', hint: 'top' } }}
          >
            {/* @TODO temporary solution until component supports ID prop and manual focusing */}
            <div id={DAY_PICKER_ID} tabIndex={-1} />
            <DateRadioChipGroup
              htmlName="daypicker"
              chips={dayOptions}
              variant="carousel"
              pageSize={PageSizeConfig}
              value={`${dayIndex}`}
              startIndex={startIndex}
              onChange={e => {
                onChangeTimeSlot(undefined);
                onChangeDay(parseInt(e.target.value, 10));
              }}
              disabled={isSubmitting}
              dateFormatFn={format}
              standalone
            />
          </FormField>

          {noAvailableSlots && (
            <Text className="text-center" variant="h6">
              {t('directSignup.schedule.noSlots')} <GhostAnchor />
            </Text>
          )}
          <div className="flex flex-col gap-e24">
            <Heading level="h4" noDefaultMargins>
              {t('directSignup.schedule.availabilityTitle', { date: format(currentDay, 'EEEE, MMMM d') })}
            </Heading>

            {availabilityByTherapist.map((therapistAvailability, index) => (
              <Card key={therapistAvailability.therapist.therapistID}>
                <ContentfulTherapistSummary
                  {...therapistBadgesData}
                  therapistName={therapistAvailability.therapistName}
                  shouldShowProfile={true}
                >
                  <div>
                    <Heading
                      level="h6"
                      noDefaultMargins
                      className="flex gap-e8 items-center"
                      onClick={() => document.getElementById(DAY_PICKER_ID)?.focus()}
                    >
                      {t('directSignup.schedule.therapistAppointmentsOn', { date: format(currentDay, 'EEE, MMM d') })}{' '}
                      {index > 1 && (
                        <Button
                          variant="link"
                          size="xs"
                          onClick={() => document.getElementById(DAY_PICKER_ID)?.focus()}
                        >
                          {t('directSignup.schedule.changeDate')}
                        </Button>
                      )}
                    </Heading>

                    <FormField
                      name={name}
                      config={{
                        order: { error: 'bottom', hint: 'top' },
                        labelSpacing: 'lg',
                      }}
                      // Note(juan): placeholder manual styles and build while we experiment with this component, will later be ported to syntax
                    >
                      <RadioChipGroup
                        htmlName={`timeslot-${therapistAvailability.therapist.therapistID}`}
                        chips={therapistAvailability.timeSlots.map(slot => ({
                          value: slot.id,
                          label: formatInTimezone(slot.time, 'h:mm a'),
                        }))}
                        variant="standard"
                        onChange={e => {
                          const pickedId = e.target.value;
                          const slot = therapistAvailability.timeSlots.find(s => s.id === pickedId);
                          if (!slot) {
                            captureException(new Error(`Unable to find slot with id: ${pickedId}`), {
                              extra: {
                                pickedId,
                                therapistAvailability,
                              },
                            });
                          }

                          onChangeTimeSlot(slot);
                        }}
                        disabled={isSubmitting}
                        value={field.value?.id ?? ''}
                        standalone
                        error={error}
                        className="[&>*]:mobile-xs-d:grid-cols-2" //@TODO will be moved to syntax
                      />
                    </FormField>
                  </div>
                </ContentfulTherapistSummary>
              </Card>
            ))}
          </div>

          <GhostAnchor />
        </>
      )}
    </>
  );
};
